// DOM overlay for `type: 'modal'` objects. Dark slate, monospace, accent border.
// ESC, click-outside, or the X button closes. While open, controller is paused by the caller.

const ACCENT_DEFAULT = '#f6b93b';

// Shared X close button. Sized to match the existing 'esc to close' hint
// register so the two affordances feel like a pair.
function makeCloseBtn(onClick) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-label', 'Close');
  btn.textContent = '×';
  btn.style.cssText = `
    background: transparent;
    border: none;
    color: rgba(244, 234, 213, 0.5);
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    padding: 4px 8px;
    margin: 0;
    font-family: inherit;
    transition: color 140ms ease;
  `;
  btn.addEventListener('mouseenter', () => { btn.style.color = '#f4ead5'; });
  btn.addEventListener('mouseleave', () => { btn.style.color = 'rgba(244, 234, 213, 0.5)'; });
  btn.addEventListener('click', (event) => {
    event.stopPropagation();
    onClick();
  });
  return btn;
}

export class ModalSystem {
  constructor() {
    this.scrim = null;
    this.panel = null;
    this.titleEl = null;
    this.bodyEl = null;
    this.isOpen = false;
    this.onClose = null;
    this._keyHandler = null;
    this._mount();
  }

  _mount() {
    const existing = document.querySelector('.iw-modal-scrim');
    if (existing) {
      this.scrim = existing;
      this.panel = existing.querySelector('.iw-modal-panel');
      this.titleEl = existing.querySelector('.iw-modal-title');
      this.bodyEl = existing.querySelector('.iw-modal-body');
      this.scrim.style.display = 'none';
      return;
    }
    const scrim = document.createElement('div');
    scrim.className = 'iw-modal-scrim';
    scrim.style.cssText = `
      position: fixed;
      inset: 0;
      background: rgba(8, 6, 5, 0.72);
      backdrop-filter: blur(2px);
      display: none;
      align-items: center;
      justify-content: center;
      z-index: 60;
      pointer-events: auto;
    `;

    const panel = document.createElement('div');
    panel.className = 'iw-modal-panel';
    panel.style.cssText = `
      position: relative;
      width: min(560px, calc(100vw - 32px));
      max-height: 80vh;
      overflow: auto;
      background: #14110e;
      color: #f4ead5;
      border: 1px solid rgba(244, 234, 213, 0.18);
      border-left: 3px solid ${ACCENT_DEFAULT};
      border-radius: 6px;
      padding: 28px 32px 32px;
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.65);
    `;

    const closeBtn = makeCloseBtn(() => this.close());
    closeBtn.style.position = 'absolute';
    closeBtn.style.top = '10px';
    closeBtn.style.right = '12px';
    panel.appendChild(closeBtn);

    const title = document.createElement('h2');
    title.className = 'iw-modal-title';
    title.style.cssText = `
      margin: 0 0 18px 0;
      font-size: 18px;
      font-weight: 600;
      letter-spacing: 0.02em;
      color: #f4ead5;
    `;

    const body = document.createElement('div');
    body.className = 'iw-modal-body';
    body.style.cssText = `
      font-size: 14px;
      line-height: 1.6;
    `;

    const hint = document.createElement('div');
    hint.textContent = 'esc to close';
    hint.style.cssText = `
      margin-top: 22px;
      font-size: 11px;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: rgba(244, 234, 213, 0.45);
    `;

    panel.appendChild(title);
    panel.appendChild(body);
    panel.appendChild(hint);
    scrim.appendChild(panel);
    document.body.appendChild(scrim);

    scrim.addEventListener('click', (event) => {
      if (event.target === scrim) this.close();
    });

    this.scrim = scrim;
    this.panel = panel;
    this.titleEl = title;
    this.bodyEl = body;
  }

  open(object, { onClose, confirm } = {}) {
    if (!object?.content) return;
    this.onClose = onClose ?? null;
    this.titleEl.textContent = object.content.title ?? object.label ?? '';
    this.bodyEl.innerHTML = '';
    const accent = object.accent ?? ACCENT_DEFAULT;
    this.panel.style.borderLeftColor = accent;

    // Optional leading image (for objects like the laptop screen showing
    // the Teams background). Renders as a framed <img> at the top of the
    // body, with optional caption text from object.content.body below.
    if (typeof object.content.image === 'string') {
      const wrap = document.createElement('div');
      wrap.style.cssText = `
        border: 1px solid rgba(244, 234, 213, 0.16);
        border-radius: 4px;
        overflow: hidden;
        background: #0a0a0a;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
        margin-bottom: 14px;
      `;
      const img = document.createElement('img');
      img.src = object.content.image;
      img.alt = object.content.title ?? '';
      img.style.cssText = `display: block; width: 100%; height: auto;`;
      wrap.appendChild(img);
      this.bodyEl.appendChild(wrap);
    }

    // Reset hint el (last child) — we'll re-add it after content
    const fields = object.content.fields;
    if (fields && typeof fields === 'object') {
      const dl = document.createElement('dl');
      dl.style.cssText = `
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 10px 22px;
        margin: 0;
      `;
      for (const [key, value] of Object.entries(fields)) {
        const dt = document.createElement('dt');
        dt.textContent = humanize(key);
        dt.style.cssText = `
          color: ${accent};
          font-size: 12px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          align-self: start;
          padding-top: 2px;
        `;
        const dd = document.createElement('dd');
        dd.textContent = value;
        dd.style.cssText = `margin: 0; color: #f4ead5;`;
        dl.appendChild(dt);
        dl.appendChild(dd);
      }
      this.bodyEl.appendChild(dl);
    } else if (typeof object.content.body === 'string') {
      // Split on blank lines so authors can use \n\n for paragraph breaks.
      const paragraphs = object.content.body.split(/\n{2,}/);
      paragraphs.forEach((text, i) => {
        const p = document.createElement('p');
        p.textContent = text;
        p.style.margin = i === 0 ? '0' : '14px 0 0';
        this.bodyEl.appendChild(p);
      });
    }

    // Confirmation buttons (link-type objects pass `confirm` callback)
    this._removeButtonRow();
    if (typeof confirm === 'function') {
      const row = document.createElement('div');
      row.className = 'iw-modal-buttons';
      row.style.cssText = `
        margin-top: 22px;
        display: flex;
        gap: 10px;
        justify-content: flex-end;
      `;

      const cancelBtn = this._makeButton('cancel', () => this.close(), {
        bg: 'transparent',
        border: 'rgba(244, 234, 213, 0.25)',
        color: '#f4ead5',
      });

      const confirmBtn = this._makeButton('open ↗', () => {
        // Run confirm callback first, then close
        try { confirm(); } finally { this.close(); }
      }, {
        bg: accent,
        border: accent,
        color: '#14110e',
      });

      row.appendChild(cancelBtn);
      row.appendChild(confirmBtn);
      this.bodyEl.parentElement.insertBefore(row, this.bodyEl.nextSibling);
    }

    this.isOpen = true;
    this.scrim.style.display = 'flex';
    this._bindKeys();
  }

  _makeButton(label, onClick, { bg, border, color }) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.style.cssText = `
      font-family: ui-monospace, "SF Mono", Menlo, monospace;
      font-size: 12px;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      padding: 8px 14px;
      border-radius: 4px;
      cursor: pointer;
      background: ${bg};
      border: 1px solid ${border};
      color: ${color};
    `;
    btn.addEventListener('click', onClick);
    return btn;
  }

  _removeButtonRow() {
    const existing = this.panel.querySelector('.iw-modal-buttons');
    if (existing) existing.remove();
  }

  _bindKeys() {
    if (this._keyHandler) return;
    this._keyHandler = (event) => {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    };
    window.addEventListener('keydown', this._keyHandler);
  }

  _unbindKeys() {
    if (this._keyHandler) {
      window.removeEventListener('keydown', this._keyHandler);
      this._keyHandler = null;
    }
  }

  close() {
    if (!this.isOpen) return;
    this._unbindKeys();
    this.isOpen = false;
    this.scrim.style.display = 'none';
    const cb = this.onClose;
    this.onClose = null;
    cb?.();
  }
}

function humanize(key) {
  return key.replace(/_/g, ' ');
}
